"use client";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Tag } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { dgxSparkModels } from "@/content/enterprise/dgx-spark-models";
import { dgxSparkSpecs, dgxSparkPromo } from "@/content/enterprise/dgx-spark";
import { AsusGx10Offer } from "@/components/shared/AsusGx10Offer";

/**
 * Shared shell for the per-OEM DGX Spark pages. The differentiating content
 * lives in dgx-spark-models.ts — this only lays it out, so the pages cannot
 * drift apart structurally while staying genuinely different in substance.
 */
export default function DgxSparkModelPage({ slug }: { slug: string }) {
  const locale = useLocale();
  const page = dgxSparkModels[slug];
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        animate: { y: 0 },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);
  if (!page) return null;

  const enquiry = slug === "asus-ascent-gx10-malaysia"
    ? "#gx10-quotation"
    : lp(`/enterprise/dgx-spark-malaysia/enquiry/?model=${encodeURIComponent(page.enquiryModel)}`);

  return (
    <main className="bg-background">
      {/* ── Hero ── */}
      <section className="pb-12 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...heroIn} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {page.brand} · {t(locale, "NVIDIA GB10")}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl">
              {t(locale, page.h1)}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted">{t(locale, page.intro)}</p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                href={enquiry}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Request a quotation")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link
                href={lp("/enterprise/dgx-spark-malaysia/")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
              >
                {t(locale, "Compare all DGX Spark builds")}
              </Link>
            </div>
          </motion.div>

          {/* ASUS uses the supplied offer artwork with accessible quote options.
              Other OEM pages retain their official product photography. */}
          {slug === "asus-ascent-gx10-malaysia" ? (
            <AsusGx10Offer locale={locale} />
          ) : (
            <motion.figure {...heroIn} className="mt-12 overflow-hidden rounded-xl border border-border bg-white">
              <img
                src={`/images/enterprise/${page.photo}`}
                alt={t(locale, `${page.brand} ${page.model} — official product photograph.`)}
                width={1380}
                height={880}
                loading="eager"
                className="mx-auto w-full max-w-3xl"
              />
              <figcaption className="border-t border-border bg-card px-5 py-3 text-xs text-neutral-muted">
                {page.brand} {page.model}. {t(locale, "Product image courtesy of")} {page.photoCredit}.
              </figcaption>
            </motion.figure>
          )}
        </div>
      </section>

      {/* ── Promotion ── */}
      {dgxSparkPromo.active && slug !== "asus-ascent-gx10-malaysia" && (
        <section className="pb-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              {...heroIn}
              className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-center"
            >
              <Tag className="h-5 w-5 flex-none text-primary" aria-hidden />
              <p className="text-sm leading-relaxed text-neutral-muted">
                <span className="font-semibold text-neutral-emphasis">
                  {t(locale, "On promotion now")}
                </span>{" "}
                ({t(locale, dgxSparkPromo.asOf)}) — {t(locale, dgxSparkPromo.body)}
              </p>
              <Link
                href={enquiry}
                className="inline-flex flex-none items-center gap-2 self-start rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis sm:ml-auto sm:self-center"
              >
                {t(locale, "Get current pricing")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Why this build ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Why this build")}
          </motion.h2>
          <motion.ul {...inView} className="mt-8 grid gap-4 md:grid-cols-2">
            {page.differentiators.map((d) => (
              <li key={d.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, d.title)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, d.detail)}</p>
              </li>
            ))}
          </motion.ul>

          <motion.div {...inView} className="mt-8 rounded-lg border border-border bg-neutral-surface p-6">
            <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
              {t(locale, "Who it suits")}
            </h3>
            <p className="mt-2 leading-relaxed text-neutral-muted">{t(locale, page.suits)}</p>
          </motion.div>
        </div>
      </section>

      {/* ── Shared GB10 platform specs ── */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "The platform underneath")}
            </h2>
            <p className="mt-3 text-neutral-muted">
              {t(
                locale,
                "Fixed by NVIDIA and identical in every OEM build, including this one — which is why the choice between them comes down to storage, thermals, security features and support rather than benchmarks.",
              )}
            </p>
          </motion.div>
          <motion.dl
            {...inView}
            className="mt-8 grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3"
          >
            {dgxSparkSpecs.map((s) => (
              <div key={s.label} className="bg-card p-4">
                <dt className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                  {t(locale, s.label)}
                </dt>
                <dd className="mt-1 text-sm font-medium text-neutral-emphasis">{t(locale, s.value)}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Co-located so /ms and /zh render it too. */}
          <StructuredData type="faq" faqs={page.faqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Common questions")}
          </motion.h2>
          <motion.dl {...inView} className="mt-8 grid gap-6 lg:grid-cols-2">
            {page.faqs.map((f) => (
              <div key={f.question} className="rounded-lg border border-border bg-card p-6">
                <dt className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </section>

      {/* ── Related builds ── */}
      <section className="border-y border-border bg-neutral-surface py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-xl font-bold text-neutral-emphasis">
            {t(locale, "Other DGX Spark builds")}
          </motion.h2>
          {/* Cross-link to this manufacturer's own brand page, where one is published.
              Added 2026-09-19: the Dell brand page sat at 134 impressions / 0 clicks /
              p43.5 with almost no inbound links. */}
          {page.brandPage ? (
            <motion.p {...inView} className="mt-6 text-sm text-neutral-muted">
              <Link
                href={lp(`/enterprise/${page.brandPage}/`)}
                className="font-semibold text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
              >
                {t(locale, "Everything else we supply from")} {page.brand}
              </Link>
            </motion.p>
          ) : null}
          <motion.ul {...inView} className="mt-6 grid gap-4 sm:grid-cols-3">
            {page.related.map((r) => {
              const o = dgxSparkModels[r];
              if (!o) return null;
              return (
                <li key={r}>
                  <Link
                    href={lp(`/enterprise/${o.slug}/`)}
                    className="flex h-full flex-col rounded-lg border border-border bg-card p-5 transition hover:border-primary/50"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-neutral-muted">
                      {o.brand}
                    </span>
                    <span className="mt-1 font-display text-base font-semibold text-neutral-emphasis">
                      {o.model}
                    </span>
                  </Link>
                </li>
              );
            })}
          </motion.ul>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="rounded-xl border border-border bg-card p-8 md:p-10">
            <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
              {t(locale, "Get a quotation in Ringgit")}
            </h2>
            <p className="mt-3 max-w-2xl text-neutral-muted">
              {t(
                locale,
                "Tell us the configuration and quantity. We come back in RM with SST stated, usually within two working days — and if a different build fits your constraint better, we will say so.",
              )}
            </p>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {[
                "Quoted in RM, SST stated",
                "MyInvois e-invoice on purchase",
                "Authorised Malaysian distribution",
                "Every OEM build quotable",
              ].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-neutral-muted">
                  <Check className="h-4 w-4 flex-none text-primary" aria-hidden />
                  {t(locale, x)}
                </li>
              ))}
            </ul>
            <Link
              href={enquiry}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "Request a quotation")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <p className="mt-8 border-t border-border pt-6 text-xs leading-relaxed text-neutral-muted">
              {t(locale, page.trademark)}
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
